import { inject, Injectable, Injector } from '@angular/core';
import { applyActionCode, Auth, AuthCredential, authState, deleteUser, sendEmailVerification, signInWithCredential, signOut, User } from '@angular/fire/auth';
import { BehaviorSubject } from 'rxjs';
import { AuthError } from './auth-error';
import { AlertConfigs, AlertType } from '@libs/models/alert.models';
import { AlertModalComponent } from '@libs/components/modal/alert/alert-modal.component';
import { ModalController } from '@ionic/angular';
import { CouponService } from '../coupon.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  protected auth: Auth = inject(Auth);
  private couponService = inject(CouponService);
  private authState$ = authState(this.auth);
  private static isInit = false;
  public isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private static _lang: string = 'br';


  protected modalCtrl: ModalController;

  constructor() {
    this.modalCtrl = inject(ModalController);
  }

  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  setLang(lang: string) {
    AuthService._lang = lang;
  }

  protected get lang() {
    return AuthService._lang;
  }

  authState() {
    return this.authState$;
  }

  authStateChange(role: string | readonly string[]) {
    if (AuthService.isInit) return;
    AuthService.isInit = true;

    this.authState$.subscribe({ next: (user: any) => {
      this.isAuthenticated.next(user != null);
      if (user) {
        this.verifyRoleAndEmail(role);
      }
    } });
  }

  async verifyRoleAndEmail(role: string | readonly string[]): Promise<void> {
    if (!this.currentUser) {
      throw new Error('No current user available');
    }

    if (this.currentUser?.metadata?.creationTime === this.currentUser?.metadata?.lastSignInTime) {
      return;
    }

    const authorizedRoles = typeof role === 'string' ? [role] : role;
    const { claims }: any = await this.currentUser.getIdTokenResult();
    if (!authorizedRoles.includes(claims.role)) {
      this.showAlertModal({
        type: AlertType.ERROR,
        title: "User role not authorized, signing out",
        confirmText: "OK",
      });
      await this.signOut();
    }
  }

  showAlertModal(configs: AlertConfigs) {
    return this.openModal(AlertModalComponent, configs, ['be-modal-alert']);
  }

  async openModal(component: any, modalConfig = {}, cssClass: string[] = [], backdropDismiss: boolean = true) {
    const modal = await this.modalCtrl.create({
      component,
      componentProps: modalConfig,
      cssClass,
      backdropDismiss: backdropDismiss
    });

    modal.present();
    return modal;
  }

  async getAuthorization() {
    if (!this.currentUser) {
      throw new Error('No current user available');
    }

    const idToken = await this.currentUser.getIdToken();
    return { Authorization: 'Bearer ' + idToken };
  }

  signInWithCredential(credential: AuthCredential) {
    return signInWithCredential(this.auth, credential);
  }

  deleteUser() {
    if (!this.currentUser) {
      throw new Error('No current user available');
    }

    return deleteUser(this.currentUser);
  }

  signOut() {
    this.couponService.clearCoupon();

    return signOut(this.auth);
  }

  async applyActionCode(oobCode: string) {
    try {
      await applyActionCode(this.auth, oobCode);

      return true;
    } catch (err) {
      throw new AuthError(err, this.lang);
    }
  }

  async sendEmailVerification() {
    try {
      await sendEmailVerification(this.currentUser!);

      return true;
    } catch (err) {
      throw new AuthError(err, this.lang);
    }
  }
}
